import { jsx as _jsx } from "react/jsx-runtime";
/**
 * @file dnd utils
 */
import React from 'react';
// eslint-disable-next-line no-restricted-imports
import { DndProvider, useDrag, useDrop } from 'react-dnd';
// eslint-disable-next-line no-restricted-imports
import { HTML5Backend } from 'react-dnd-html5-backend';
function useDNDProviderElement(props) {
    if (!props.children)
        return null;
    // "context={window}" is for resolve "Error: Cannot have two HTML5 backends at the same time" , issue by : https://github.com/react-dnd/react-dnd/issues/3119
    return _jsx(DndProvider, Object.assign({ backend: HTML5Backend, context: window }, { children: props.children }), void 0);
}
/**
 * Replacement of <DndProvider />
 * HACK FOR HTML5Backend Issue from https://github.com/react-dnd/react-dnd/issues/186
 * 大概是如果按 dnd 的 demo 使用 DndProvider 的话，当 DndProvider 被重新渲染时，传入的 HTML5Backend 会重新构造一个值，从而导致前后值发生冲突
 * 所以此处是用来稳定 HTML5Backend 的。
 *
 * @param props - props
 * @param props.children - 子元素
 * @returns DndProvider
 */
export function DragAndDrop(props) {
    const DNDElement = useDNDProviderElement(props);
    return _jsx(React.Fragment, { children: DNDElement }, void 0);
}
/**
 * 获取上下拖拽排序项的 drag 配置
 *
 * @param props - props
 * @param props.id - 拖拽项的唯一标识
 * @param props.index - 拖拽项的下标
 * @param props.acceptType - 定义放置目标接收的拖拽目标类型
 * @param props.onMove - 移动的回调
 * @returns drag 配置
 */
export const useSortableDragItem = ({ id, index, acceptType, onMove, }) => {
    const dropRef = React.useRef(null);
    const [, drop] = useDrop({
        accept: acceptType,
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover(item, monitor) {
            var _a;
            if (!dropRef.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;
            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return;
            }
            // Determine rectangle on screen
            const hoverBoundingRect = (_a = dropRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            // Determine mouse position
            const clientOffset = monitor.getClientOffset();
            // Get pixels to the top
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;
            /*
             * Only perform the move when the mouse has crossed half of the items height
             * When dragging downwards, only move when the cursor is below 50%
             * When dragging upwards, only move when the cursor is above 50%
             */
            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }
            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }
            // Time to actually perform the action
            onMove(dragIndex, hoverIndex);
            /*
             * Note: we're mutating the monitor item here!
             * Generally it's better to avoid mutations,
             * but it's good here for the sake of performance
             * to avoid expensive index searches.
             */
            // eslint-disable-next-line no-param-reassign
            item.index = hoverIndex;
        },
    });
    const [{ isDragging }, drag, preview] = useDrag({
        type: acceptType,
        item: () => {
            return { id, index };
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });
    preview(drop(dropRef));
    return {
        /**
         * 触发拖拽的容器
         */
        dragRef: drag,
        /**
         * 放置目标容器
         */
        dropRef,
        /**
         * 是否拖拽中
         */
        isDragging,
    };
};
/**
 * 获取拖拽排序后的列表数据
 *
 * @param items - 要排序的列表
 * @param dragIndex - 正在拖拽的数据下标
 * @param hoverIndex - 拖拽目标要放置的位置下标
 * @returns 排序后的列表
 */
export const getSortedDraggableItems = (items, dragIndex, hoverIndex) => {
    const dragItem = items[dragIndex];
    if (!dragItem)
        return items;
    const newItems = [...items];
    newItems.splice(dragIndex, 1);
    newItems.splice(hoverIndex, 0, dragItem);
    return newItems;
};
//# sourceMappingURL=data:application/json;base64,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